import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useAuth from 'src/hooks/useAuth';
import { userService } from 'src/services/User.service';

interface FeatureFlagState {
  featureFlags: {
    flags?: Record<string, boolean>;
  };
}

interface RenterNotificationGate {
  flagEnabled: boolean;
  propertyEnabled: boolean;
  isEnabled: boolean;
  loading: boolean;
}

export const useRenterNotificationGate = (): RenterNotificationGate => {
  const { user } = useAuth();
  const renterNotificationsEnabled = useSelector(
    (state: FeatureFlagState) =>
      state.featureFlags.flags?.HOUSEHOLD_NOTIFICATIONS_ENABLED
  );
  const [propertyEnabled, setPropertyEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const userId = user?.id;
    const isResident = Boolean(
      user?.isResident ||
        user?.role === 'resident' ||
        user?.groups?.some((group) => group?.name?.toLowerCase() === 'resident')
    );
    const hasEnabledPropertyFromCertifications = (certifications): boolean =>
      Array.isArray(certifications) &&
      certifications.some((cert) => {
        const directPropertyEnabled =
          cert?.property?.enable_renter_notifications;
        const configPropertyEnabled =
          cert?.certification_config?.properties?.some(
            (property) => property?.enable_renter_notifications
          );
        return Boolean(directPropertyEnabled || configPropertyEnabled);
      });

    if (!userId || !isResident || !renterNotificationsEnabled) {
      setPropertyEnabled(false);
      setLoading(false);
      return;
    }

    if (
      hasEnabledPropertyFromCertifications(user?.activeCertifications) ||
      hasEnabledPropertyFromCertifications(
        user?.currentCertification ? [user.currentCertification] : []
      )
    ) {
      setPropertyEnabled(true);
      setLoading(false);
      return;
    }

    const userHouseholdMembers = user?.household_members || [];
    if (userHouseholdMembers.length > 0) {
      const hasEnabledProperty = userHouseholdMembers.some((member) => {
        const household = member?.household;
        const directPropertyEnabled =
          household?.property?.enable_renter_notifications;
        const certificationPropertyEnabled = household?.certifications?.some(
          (cert) => cert?.property?.enable_renter_notifications
        );
        const configPropertyEnabled = household?.certifications?.some((cert) =>
          cert?.certification_config?.properties?.some(
            (property) => property?.enable_renter_notifications
          )
        );
        return Boolean(
          directPropertyEnabled ||
            certificationPropertyEnabled ||
            configPropertyEnabled
        );
      });
      setPropertyEnabled(Boolean(hasEnabledProperty));
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadPropertyGate = async (): Promise<void> => {
      setLoading(true);
      try {
        const response = await userService.fetchUserProfileWithHouseholdMembers(
          userId
        );
        const householdMembers = response?.data?.household_members || [];
        const hasEnabledProperty = householdMembers.some((member) => {
          const household = member?.household;
          const directPropertyEnabled =
            household?.property?.enable_renter_notifications;
          const certificationPropertyEnabled = household?.certifications?.some(
            (cert) => cert?.property?.enable_renter_notifications
          );
          const configPropertyEnabled = household?.certifications?.some(
            (cert) =>
              cert?.certification_config?.properties?.some(
                (property) => property?.enable_renter_notifications
              )
          );
          return Boolean(
            directPropertyEnabled ||
              certificationPropertyEnabled ||
              configPropertyEnabled
          );
        });
        if (!cancelled) {
          setPropertyEnabled(Boolean(hasEnabledProperty));
        }
      } catch {
        if (!cancelled) {
          setPropertyEnabled(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPropertyGate();

    return () => {
      cancelled = true;
    };
  }, [renterNotificationsEnabled, user]);

  return {
    flagEnabled: Boolean(renterNotificationsEnabled),
    propertyEnabled,
    isEnabled: Boolean(renterNotificationsEnabled) && propertyEnabled,
    loading,
  };
};
